(function () {
  "use strict";

  const SELECTORS = {
    link: "[data-zeus-help-center-topic-link]",
    panel: "[data-zeus-help-center-panel]",
    question: "[data-zeus-help-center-question]",
    quickLink: "[data-zeus-help-center-quick-link]",
  };

  class ZeusHelpCenter extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.links = Array.from(this.querySelectorAll(SELECTORS.link));
      this.panels = Array.from(this.querySelectorAll(SELECTORS.panel));
      this.topics = new Map(this.panels.map((panel) => [panel.dataset.topic, panel]));

      if (this.links.length === 0 || this.topics.size === 0) return;

      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };

      this.addEventListener("click", this.handleTopicClick, {
        ...listenerOptions,
        capture: true,
      });
      window.addEventListener("hashchange", this.handleLocationChange, listenerOptions);
      window.addEventListener("popstate", this.handleLocationChange, listenerOptions);
      document.addEventListener("shopify:block:select", this.handleBlockSelect, listenerOptions);

      const hashTopic = this.getTopicFromHash(window.location.hash);
      const defaultTopic = this.dataset.defaultTopic;
      this.fallbackTopic = this.topics.has(defaultTopic) ? defaultTopic : this.links[0].dataset.topic;
      const initialTopic = hashTopic || this.fallbackTopic;

      this.dataset.enhanced = "";
      this.activateTopic(initialTopic, { focusFallback: false, scroll: false });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    handleTopicClick = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const link = event.target.closest(SELECTORS.link);
      if (!link || !this.contains(link) || !this.topics.has(link.dataset.topic)) return;

      event.preventDefault();
      event.stopPropagation();

      const topic = link.dataset.topic;
      const topicWasActive = this.dataset.activeTopic === topic;
      this.activateTopic(topic, { focusFallback: false, scroll: true });

      if (!topicWasActive && window.location.hash !== link.hash) {
        window.history.pushState({}, "", link.hash);
      }
    };

    handleLocationChange = () => {
      const hash = window.location.hash;
      const topic = hash ? this.getTopicFromHash(hash) : this.fallbackTopic;
      if (!topic) return;

      this.activateTopic(topic, { focusFallback: true, scroll: false });
    };

    handleBlockSelect = (event) => {
      if (!this.contains(event.target)) return;

      const question = event.target.closest(SELECTORS.question);
      if (question && this.topics.has(question.dataset.topic)) {
        this.activateTopic(question.dataset.topic, { focusFallback: false, scroll: false });
        question.open = true;
        question.scrollIntoView({ behavior: "auto", block: "nearest" });
        return;
      }

      const blockId = event.target.dataset.blockId;
      if (!blockId) return;

      const copies = Array.from(
        this.querySelectorAll(`${SELECTORS.quickLink}[data-block-id="${CSS.escape(blockId)}"]`),
      );
      const visibleCopy = copies.find((copy) => copy.getClientRects().length > 0);
      visibleCopy?.scrollIntoView({ behavior: "auto", block: "nearest" });
    };

    getTopicFromHash(hash) {
      if (!hash) return null;

      const link = this.links.find((candidate) => candidate.hash === hash);
      return link?.dataset.topic || null;
    }

    activateTopic(topic, { focusFallback, scroll }) {
      const nextPanel = this.topics.get(topic);
      if (!nextPanel) return;

      const activeElement = document.activeElement;
      const focusWillBeHidden = this.panels.some((panel) => panel !== nextPanel && panel.contains(activeElement));

      for (const link of this.links) {
        if (link.dataset.topic === topic) {
          link.setAttribute("aria-current", "true");
        } else {
          link.removeAttribute("aria-current");
        }
      }

      for (const panel of this.panels) {
        panel.hidden = panel !== nextPanel;
      }

      this.dataset.activeTopic = topic;

      const nextHeading = nextPanel.querySelector("[tabindex='-1']");
      if (focusWillBeHidden && focusFallback && nextHeading) {
        nextHeading.focus({ preventScroll: true });
      }

      if (scroll && window.matchMedia("(max-width: 699px)").matches) {
        this.scrollHeadingIntoView(nextHeading);
      }
    }

    scrollHeadingIntoView(heading) {
      if (!heading) return;

      const rect = heading.getBoundingClientRect();
      const header = document.querySelector(".shopify-section-group-header-group");
      const headerOffset = header?.getBoundingClientRect().height || 0;
      const isVisible = rect.top >= headerOffset && rect.bottom <= window.innerHeight;

      if (!isVisible) {
        heading.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start",
        });
      }
    }
  }

  if (!customElements.get("zeus-help-center")) {
    customElements.define("zeus-help-center", ZeusHelpCenter);
  }
})();
